import { Content } from '@amzn/awscat-pattern-library-service-model';
import { v4 as uuid } from 'uuid';

import { deepCloneContent } from '../../model/CloneContent';
import { log } from '../../loggers/Logger';
import { SyncAdhocMigration } from '../AdhocMigration';

/** Title of the Epics section in legacy templates. */
const LEGACY_EPICS_SECTION_TITLE = 'Epics';

interface EpicLike {
  readonly id: string;
  readonly title: string;
}

/**
 * In older versions of APG, an epic's id was the epic's title, so renaming
 * an epic changed its id and broke linked tasks and comments. This
 * migration replaces such ids with fresh UUIDs.
 */
export class EpicIdMigration implements SyncAdhocMigration {
  readonly kind = 'sync' as const;
  readonly name = 'epic-id';

  private logger = log.child({ class: this.constructor.name, migration: this.name });

  apply(content: Content): Content {
    // Find the Epics section by title (legacy templates used title-based lookup).
    const epicsIdx =
      content.contentData?.contentSections?.findIndex(
        (section) => section.title === LEGACY_EPICS_SECTION_TITLE,
      ) ?? -1;
    if (epicsIdx === -1) {
      this.logger.info('Skipping: no Epics section found');
      return content;
    }

    // Bail if contentData isn't the expected array-of-epics shape.
    const epics = content.contentData.contentSections[epicsIdx];
    // @ts-expect-error TS18048 - strict migration
    if (!Array.isArray(epics.contentData)) {
      this.logger.info('Skipping: Epics section contentData is not an array');
      return content;
    }

    // Count epics whose id still equals their title (the legacy pattern).
    // @ts-expect-error TS18048 - strict migration
    const legacyCount = (epics.contentData as readonly EpicLike[]).filter(
      (e) => e.id === e.title,
    ).length;
    if (legacyCount === 0) {
      this.logger.info('Skipping: no legacy epic ids found');
      return content;
    }

    // Deep-clone then replace legacy ids with fresh UUIDs.
    const updated = deepCloneContent(content);
    const updatedEpics = updated.contentData.contentSections[epicsIdx];
    // @ts-expect-error TS18048 - strict migration
    updatedEpics.contentData = (updatedEpics.contentData as readonly EpicLike[]).map((e) =>
      e.id === e.title ? { ...e, id: uuid() } : e,
    );

    this.logger.info({ replaced: legacyCount }, 'Replaced legacy epic ids with UUIDs');
    return updated;
  }
}
